import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { UserCheck, Users, Truck, Search, RefreshCw, CalendarCheck, Trash2, Contact } from 'lucide-react';
import { toast } from 'sonner';

const PRODUCT_ENQUIRY_LABELS = {
  meeting_room: 'Meeting Room',
  conference_room: 'Conference Room',
  day_pass: 'Day Pass',
  dedicated_desk: 'Dedicated Desk',
  dedicated_cabin: 'Dedicated Cabin',
  event_space: 'Event Space',
  virtual_office: 'Virtual Office',
  podcast_spaces: 'Podcast Spaces',
};

export const VMSTab = ({ api, venues = [], ownOnly = false, canDelete = false }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.visitor_type = typeFilter;
      if (venueFilter !== 'all') params.venue_id = venueFilter;
      const res = await api.get('/visitor-logs', { params });
      setList(res.data || []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [typeFilter, venueFilter]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/visitor-logs/${deleteTarget.id}`);
      toast.success('Visitor record deleted');
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete visitor record');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(v =>
      (v.visitor_name || '').toLowerCase().includes(q) ||
      (v.visitor_email || '').toLowerCase().includes(q) ||
      (v.visitor_phone || '').toLowerCase().includes(q)
    );
  }, [list, search]);

  const today = new Date().toISOString().split('T')[0];
  const kpis = useMemo(() => ({
    total: list.length,
    clients: list.filter(v => v.visitor_type === 'client').length,
    vendors: list.filter(v => v.visitor_type === 'vendor').length,
    guests: list.filter(v => v.visitor_type === 'guest').length,
    today: list.filter(v => v.visit_date === today).length,
  }), [list, today]);

  return (
    <div className="space-y-6" data-testid="vms-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total Visitors" value={kpis.total} icon={<UserCheck className="w-4 h-4 text-primary" />} />
        <KpiCard label="Clients" value={kpis.clients} icon={<Users className="w-4 h-4 text-emerald-500" />} />
        <KpiCard label="Vendor" value={kpis.vendors} icon={<Truck className="w-4 h-4 text-amber-500" />} />
        <KpiCard label="Guests" value={kpis.guests} icon={<Contact className="w-4 h-4 text-sky-500" />} />
        <KpiCard label="Today" value={kpis.today} icon={<CalendarCheck className="w-4 h-4 text-blue-500" />} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><UserCheck className="w-5 h-5 text-primary" /> Visitor Management System</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {ownOnly
                ? 'Client, Vendor, and Guest visitor records you have logged.'
                : 'All Client, Vendor, and Guest visitor records logged by Center Managers.'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="vms-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by name, email or phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="vms-search" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="sm:w-48" data-testid="vms-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="guest">Guest</SelectItem>
              </SelectContent>
            </Select>
            <Select value={venueFilter} onValueChange={setVenueFilter}>
              <SelectTrigger className="sm:w-56" data-testid="vms-venue-filter"><SelectValue placeholder="All Venues" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <p className="text-center text-sm text-muted-foreground py-8">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No visitor records found.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Visitor</th>
                    <th className="px-3 py-2">Contact</th>
                    <th className="px-3 py-2">Venue</th>
                    <th className="px-3 py-2">Visit Date</th>
                    <th className="px-3 py-2">Details</th>
                    <th className="px-3 py-2">Notes</th>
                    <th className="px-3 py-2">Logged By</th>
                    {canDelete && <th className="px-3 py-2 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => {
                    const isVendor = v.visitor_type === 'vendor';
                    const isGuest = v.visitor_type === 'guest';
                    return (
                      <tr key={v.id} className="border-t hover:bg-muted/20" data-testid={`vms-row-${v.id}`}>
                        <td className="px-3 py-2">
                          {isVendor
                            ? <Badge variant="secondary" className="text-xs bg-amber-500/15 text-amber-700">Vendor</Badge>
                            : isGuest
                            ? <Badge variant="secondary" className="text-xs bg-sky-500/15 text-sky-700">Guest</Badge>
                            : <Badge className="text-xs bg-emerald-500/15 text-emerald-700">Client</Badge>}
                        </td>
                        <td className="px-3 py-2 font-medium">{v.visitor_name || '—'}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {v.visitor_email && <div>{v.visitor_email}</div>}
                          {v.visitor_phone && <div>{v.visitor_phone}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">{v.venue_name || '—'}</td>
                        <td className="px-3 py-2 text-xs">{v.visit_date || '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          {isGuest
                            ? <>{v.start_time || '—'}{v.host_name ? <div className="text-muted-foreground">Meeting: {v.host_name}{v.host_company ? ` (${v.host_company})` : ''}</div> : null}</>
                            : isVendor
                            ? <>{v.start_time || '—'}</>
                            : <>{PRODUCT_ENQUIRY_LABELS[v.product_enquiry] || v.product_enquiry || '—'} · {v.seats_required || 1} seat(s)</>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[320px] min-w-[220px] align-top" title={v.notes || ''}>
                          <div className="line-clamp-2 break-words">{v.notes || '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {v.logged_by_name || '—'}<br />
                          {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}
                        </td>
                        {canDelete && (
                          <td className="px-3 py-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteTarget(v)}
                              data-testid={`vms-delete-btn-${v.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {canDelete && (
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete visitor record?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the record for{' '}
                {deleteTarget?.visitor_name ? `"${deleteTarget.visitor_name}"` : 'this visitor'}. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {deleting ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, icon }) => (
  <Card>
    <CardContent className="p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <p className="text-2xl font-heading font-bold mt-1">{value}</p>
    </CardContent>
  </Card>
);

export default VMSTab;